/**
 * AIModelFeatureRow (VH-258, extended by VH-280)
 *
 * One AI feature on Admin → AI models: the model it runs, and for OpenAI features the
 * model a call retries on once when OpenAI says the first is gone (the fallback) and how
 * hard a reasoning model thinks (effort). A feature that has fallen back since its
 * setting last changed is marked red with the missing model's name — the point of the
 * mark is that a missed retirement cannot go unnoticed. Saving the feature clears it.
 *
 * `readOnly` rows are the photo analyzers, whose model is set on LLM Settings; they are
 * listed here so that every OpenAI call VitaHub makes is on one screen. Since HP-34
 * every row is read-only while HubPlatform holds the settings (`hubManaged`).
 */
import { useTranslation } from 'react-i18next'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import AIUsageLine from './AIUsageLine'
import { modelLabel } from './aiFormat'

function RetiringBadge({ t }) {
  return (
    <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
      {t('admin.aiModels.retiringTag')}
    </span>
  )
}

export default function AIModelFeatureRow({
  feature: f,
  choice,
  onChange,
  retiring = new Set(),
  efforts = [],
  usage,
  onClearMark,
  busy = false,
  readOnly = false,
  hubManaged = false,
}) {
  const { t } = useTranslation()
  const tag = t('admin.aiModels.retiringTag')
  const isOpenAI = f.provider === 'openai'
  const model = choice?.model ?? f.model
  const fallback = choice?.fallback ?? f.fallback_model ?? ''
  const effort = choice?.effort ?? f.effort ?? f.effort_default ?? ''
  const models = f.available_models ?? []
  // The running fallback stays selectable even if the provider stopped listing it.
  const fallbackOptions = [...new Set([...(fallback ? [fallback] : []), ...models])].filter((m) => m !== model)

  return (
    <li
      className={`py-3 grid gap-2 lg:grid-cols-[1fr,auto] lg:items-start ${
        f.missing_model ? 'border-l-4 border-red-500 pl-3 bg-red-50' : ''}`}
      data-testid={`ai-feature-${f.key}`}
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">
          {f.label}
          {f.retiring && <RetiringBadge t={t} />}
        </p>
        {f.note && !(hubManaged && f.editable === false) && <p className="text-xs text-gray-500">{f.note}</p>}
        <p className="text-xs text-gray-500">
          {f.provider}
          {!f.is_default && ` · ${t('admin.aiModels.defaultWas', { model: f.default_model })}`}
        </p>
        {f.missing_model && (
          <div className="mt-1 text-xs text-red-700 flex flex-wrap items-center gap-2" role="alert"
            data-testid={`ai-missing-${f.key}`}>
            <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>
              {t('admin.aiModels.missing', { model: f.missing_model, fallback: f.fallback_model || '–' })}
            </span>
            {!readOnly && onClearMark && (
              <button type="button" className="underline" disabled={busy}
                onClick={() => onClearMark(f)} data-testid={`clear-mark-${f.key}`}>
                {t('admin.aiModels.clearMark')}
              </button>
            )}
            {readOnly && f.editable === false && !hubManaged && <span>{t('admin.aiModels.missingAnalyzer')}</span>}
          </div>
        )}
        <AIUsageLine usage={usage} />
      </div>

      {readOnly ? (
        <dl className="text-sm grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
          <dt className="text-gray-500">{t('admin.aiModels.columns.model')}</dt>
          <dd className="text-gray-900">{modelLabel(f.model, retiring, tag)}</dd>
          {isOpenAI && (
            <>
              <dt className="text-gray-500">{t('admin.aiModels.columns.fallback')}</dt>
              <dd className="text-gray-900">{f.fallback_model || t('admin.aiModels.noFallback')}</dd>
            </>
          )}
          {isOpenAI && (f.effort || f.effort_default) && (
            <>
              <dt className="text-gray-500">{t('admin.aiModels.columns.effort')}</dt>
              <dd className="text-gray-900">{t(`admin.aiModels.efforts.${f.effort || f.effort_default}`)}</dd>
            </>
          )}
        </dl>
      ) : (
        <div className="grid gap-2 sm:grid-cols-3 lg:w-[40rem]">
          <label className="text-xs text-gray-600 flex flex-col gap-1">
            {t('admin.aiModels.columns.model')}
            <select className="text-sm w-full" aria-label={f.label} value={model}
              onChange={(e) => onChange(f.key, { model: e.target.value })}>
              {models.map((m) => <option key={m} value={m}>{modelLabel(m, retiring, tag)}</option>)}
            </select>
          </label>
          {isOpenAI && (
            <>
              <label className="text-xs text-gray-600 flex flex-col gap-1">
                {t('admin.aiModels.columns.fallback')}
                <select className="text-sm w-full" value={fallback}
                  aria-label={t('admin.aiModels.fallbackFor', { feature: f.label })}
                  onChange={(e) => onChange(f.key, { fallback: e.target.value })}>
                  <option value="">
                    {t('admin.aiModels.fallbackDefault', { model: f.fallback_default || '–' })}
                  </option>
                  {fallbackOptions.map((m) => <option key={m} value={m}>{modelLabel(m, retiring, tag)}</option>)}
                </select>
              </label>
              <label className="text-xs text-gray-600 flex flex-col gap-1">
                {t('admin.aiModels.columns.effort')}
                <select className="text-sm w-full" value={effort}
                  aria-label={t('admin.aiModels.effortFor', { feature: f.label })}
                  onChange={(e) => onChange(f.key, { effort: e.target.value })}>
                  {efforts.map((e) => <option key={e} value={e}>{t(`admin.aiModels.efforts.${e}`)}</option>)}
                </select>
                {!f.supports_effort && (
                  <span className="text-gray-500">{t('admin.aiModels.effortIgnored')}</span>
                )}
              </label>
            </>
          )}
        </div>
      )}
    </li>
  )
}
